/**
 * Facturas CXP — pantalla unificada de facturas de suplidores.
 *
 * La vista principal es el mantenimiento de facturas embebido (ares8520:
 * listado por fechas/suplidor, detalle de chasis y anulación de facturas
 * sin pagos). Tarjetas de acción:
 *   - "Registrar factura" (ares8510): factura general de suplidor sin
 *     motores, en diálogo a pantalla completa; al guardar, se recarga.
 *   - "Chasis de factura" (ares8550): modifica costo/precios de los chasis
 *     de una factura o los elimina del inventario (diálogo con barra Volver).
 *
 * Sustituye las entradas de menú de facturageneralcxp.js,
 * mantenimientofacturascxp.js y chasisfacturacxp.js.
 */
import React, { useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PriceChangeOutlinedIcon from "@mui/icons-material/PriceChangeOutlined";
import { useMaterialUIController } from "context";
import { FP, PageHeader, ActionCard, BarraVolver } from "components/financepro";
import { tienePermisosModulo } from "lib/seguridad/permisosPorModulo";
import FacturaGeneralCxp from "modules/cuentasxpagar/facturageneralcxp";
import MantenimientoFacturasCxp from "modules/cuentasxpagar/mantenimientofacturascxp";
import ChasisFacturaCxp from "modules/cuentasxpagar/chasisfacturacxp";
import useAccionInicial from "lib/useAccionInicial";

export default function CxpFacturas() {
  const [controller] = useMaterialUIController();
  const { roleDetalles } = controller;

  // Permisos por programa ARES (el backend re-valida en permisos.py).
  const puedeRegistrar = tienePermisosModulo(roleDetalles, "ares8510");
  const puedeMantenimiento = tienePermisosModulo(roleDetalles, "ares8520");
  const puedeChasis = tienePermisosModulo(roleDetalles, "ares8550");

  const [registroOpen, setRegistroOpen] = useState(false);
  const [chasisOpen, setChasisOpen] = useState(false);

  // Enlace profundo desde el buscador global (?accion=...).
  useAccionInicial({
    registrar: { permitido: puedeRegistrar, abrir: () => setRegistroOpen(true) },
    chasis: { permitido: puedeChasis, abrir: () => setChasisOpen(true) },
  });

  // Incrementarlo recarga el listado embebido (tras registrar o modificar).
  const [refreshKey, setRefreshKey] = useState(0);
  const refrescar = () => setRefreshKey((k) => k + 1);

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Facturas de Suplidores"
        subtitle="Registra, consulta y anula facturas de cuentas por pagar en un solo lugar"
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {(puedeRegistrar || puedeChasis) && (
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            {puedeRegistrar && (
              <Grid item xs={12} sm={6}>
                <ActionCard
                  icon={ReceiptLongOutlinedIcon}
                  titulo="Registrar factura"
                  descripcion="Factura general de suplidor (sin motores)"
                  color={FP.color.success}
                  onClick={() => setRegistroOpen(true)}
                />
              </Grid>
            )}
            {puedeChasis && (
              <Grid item xs={12} sm={6}>
                <ActionCard
                  icon={PriceChangeOutlinedIcon}
                  titulo="Chasis de factura"
                  descripcion="Modifica costo y precios de los chasis, o elimínalos"
                  color={FP.color.accentBlue}
                  onClick={() => setChasisOpen(true)}
                />
              </Grid>
            )}
          </Grid>
        )}

        {/* Vista principal: mantenimiento de facturas (anular/detalle) */}
        {puedeMantenimiento && <MantenimientoFacturasCxp embedded refreshToken={refreshKey} />}
      </Box>

      {/* Registrar factura general (formulario a pantalla completa) */}
      <Dialog fullScreen open={registroOpen} onClose={() => setRegistroOpen(false)}>
        <FacturaGeneralCxp embedded onExit={() => { setRegistroOpen(false); refrescar(); }} onSaved={refrescar} />
      </Dialog>

      {/* Chasis de factura (pantalla existente, con barra Volver) */}
      <Dialog fullScreen open={chasisOpen} onClose={() => setChasisOpen(false)}>
        <BarraVolver onExit={() => { setChasisOpen(false); refrescar(); }} />
        <ChasisFacturaCxp />
      </Dialog>
    </Box>
  );
}
